// src/api/audioVerificationService.js
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/metadata/audio`;

// Create axios instance with authentication
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// ========== EXPERT ENDPOINTS ==========

export const fetchAudioInfo = async () => {
  try {
    const response = await api.get(`/info`);
    return response.data;
  } catch (error) {
    console.error('Error fetching audio info:', error);
    throw error;
  }
};

export const fetchAudio = async (surahId, audio_info_id = '2') => {
  try {
    const response = await api.get(`/${surahId}`, {
      params: { audio_info_id }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching surah:', error);
    throw error;
  }
};

export const verifyAudio = async (ayahKey, audio_info_id, status, comment) => {
  try {
    const response = await api.post(`/verification`, {
      ayah_key: ayahKey,
      verification_status: status,
      audio_info_id,
      comments: comment || ''
    });
    return response.data;
  } catch (error) {
    console.error('Error verifying audio:', error);
    throw new Error(error.response?.data?.detail || 'Failed to verify audio');
  }
};

export const deleteVerification = async (ayah_key, audio_info_id) => {
  try {
    const response = await api.delete(`/verification`, {
      params: { ayah_key, audio_info_id }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting verification:', error);
    throw new Error(error.response?.data?.detail || 'Failed to delete verification');
  }
};

export const fetchAudioSuperExpert = async (surahId, audio_info_id = '2') => {
  try {
    const response = await api.get(`/expert/${surahId}`, {
      params: { audio_info_id }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching expert surah:', error);
    throw error;
  }
};

// ========== SUPER_EXPERT ENDPOINTS ==========

export const fetchAudioForApproval = async (surahId, audio_info_id = '2') => {
  try {
    const response = await api.get(`/super-expert/${surahId}`, {
      params: { audio_info_id }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching audio for approval:', error);
    throw new Error(error.response?.data?.detail || 'Failed to fetch audio for approval');
  }
};

export const approveAudio = async (ayahKey, audio_info_id, status, notes) => {
  try {
    const response = await api.post(`/approval`, {
      ayah_key: ayahKey,
      audio_info_id,
      approval_status: status,
      approval_notes: notes || ''
    });
    return response.data;
  } catch (error) {
    console.error('Error approving audio:', error);
    throw new Error(error.response?.data?.detail || 'Failed to approve audio');
  }
};

export const revokeApproval = async (ayah_key, audio_info_id) => {
  try {
    const response = await api.delete(`/approval`, {
      params: { ayah_key, audio_info_id }
    });
    return response.data;
  } catch (error) {
    console.error('Error revoking approval:', error);
    throw new Error(error.response?.data?.detail || 'Failed to revoke approval');
  }
};

export const fetchApprovalStats = async () => {
  try {
    const response = await api.get(`/approval-stats`);
    return response.data;
  } catch (error) {
    console.error('Error fetching approval stats:', error);
    throw error;
  }
};